import { handleServiceCall } from "@services/helpers"

// =====================
// Types
// =====================

/**
 * Résultat de recherche d'adresse (format API adresse.data.gouv.fr)
 */
export type AddressSearchResult = {
    /**
     * Libellé complet de l'adresse
     */
    label: string
    /**
     * Numéro de rue
     */
    housenumber?: string
    /**
     * Nom de la rue
     */
    street?: string
    /**
     * Nom (pour les lieux-dits)
     */
    name?: string
    /**
     * Code postal
     */
    postcode: string
    /**
     * Nom de la ville
     */
    city: string
    /**
     * Code INSEE de la ville
     */
    citycode?: string
    /**
     * Coordonnées géographiques [longitude, latitude]
     */
    coordinates?: [number, number]
}

/**
 * Feature GeoJSON retournée par l'API adresse.data.gouv.fr
 */
type AddressFeature = {
    type: "Feature"
    geometry: {
        type: "Point"
        coordinates: [number, number]
    }
    properties: {
        label: string
        score: number
        housenumber?: string
        id: string
        name: string
        postcode: string
        citycode: string
        x: number
        y: number
        city: string
        context: string
        type: string
        importance: number
        street?: string
    }
}

/**
 * Réponse de l'API adresse.data.gouv.fr
 */
type AddressApiResponse = {
    type: "FeatureCollection"
    version: string
    features: AddressFeature[]
    attribution: string
    licence: string
    query: string
    limit: number
}

/**
 * Paramètres de recherche d'adresse
 */
export type SearchAddressParams = {
    /**
     * Texte de recherche
     */
    search: string
    /**
     * Nombre maximum de résultats (défaut: 10)
     */
    limit?: number
    /**
     * AbortController pour annuler la requête
     */
    controller?: AbortController
}

// =====================
// Service
// =====================

/**
 * Service de recherche d'adresse via l'API adresse.data.gouv.fr
 */
export class AddressService {
    private readonly API_URL = "https://api-adresse.data.gouv.fr/search/"

    /**
     * Recherche des adresses via l'API adresse.data.gouv.fr
     * @param params - Paramètres de recherche
     * @returns Liste des adresses correspondantes
     */
    async searchAddress(
        params: SearchAddressParams
    ): Promise<AddressSearchResult[]> {
        return handleServiceCall<AddressSearchResult[]>(async () => {
            if (!params.search || params.search.trim().length < 3) {
                return []
            }

            const url = new URL(this.API_URL)
            url.searchParams.set("q", params.search)
            url.searchParams.set("limit", String(params.limit || 10))

            const response = await fetch(url.toString(), {
                signal: params.controller?.signal,
            })

            if (!response.ok) {
                throw new Error(
                    `Erreur lors de la recherche d'adresse: ${response.status}`
                )
            }

            const data: AddressApiResponse = await response.json()

            return data.features.map((feature) => ({
                label: feature.properties.label,
                housenumber: feature.properties.housenumber,
                street: feature.properties.street,
                name: feature.properties.name,
                postcode: feature.properties.postcode,
                city: feature.properties.city,
                citycode: feature.properties.citycode,
                coordinates: feature.geometry.coordinates,
            }))
        })
    }
}
